import { useEffect, useRef, useState } from 'react';
import { Button } from './Button';

/**
 * Rename a thing in place.
 *
 * One control for every object a shop names — projects, vendors, stock locations. Renaming
 * is the whole of what "edit" means for most of them, and three bespoke forms would drift
 * into three different behaviours for Escape, empty input and the pending state.
 *
 * Escape abandons rather than commits. A rename you didn't mean to make is silent damage:
 * nothing looks broken afterwards, it just isn't called what it was.
 *
 * The button and the box are named after `value`, the thing being renamed: in a list, a
 * screen reader hears "Rename Repair" and "Rename Maintenance" rather than five buttons all
 * called "Rename". The visible word leads the name, so a spoken "click Rename" still works.
 */
export function InlineRename({
  value,
  onSave,
  busy = false,
  label = 'Rename',
}: {
  value: string;
  onSave: (next: string) => void;
  busy?: boolean;
  label?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  // Saving or cancelling unmounts the box that held the focus: hand it back to Rename, or a
  // keyboard reader starts again from the top of the page (LESSONS 2026-10-05).
  const button = useRef<HTMLButtonElement>(null);
  const wasEditing = useRef(false);
  useEffect(() => {
    if (wasEditing.current && !editing) button.current?.focus();
    wasEditing.current = editing;
  }, [editing]);

  if (!editing) {
    return (
      <Button
        ref={button}
        variant="ghost"
        aria-label={`${label} ${value}`}
        onClick={() => {
          setDraft(value);
          setEditing(true);
        }}
      >
        {label}
      </Button>
    );
  }

  const commit = () => {
    const next = draft.trim();
    // A no-op save still costs a round trip and an invalidation; an empty one is a typo.
    if (next && next !== value) onSave(next);
    setEditing(false);
  };

  return (
    <span className="flex min-w-0 flex-wrap items-center gap-2">
      <input
        className="h-9 min-w-0 flex-1 rounded-[var(--radius)] border border-border bg-surface px-2 text-sm"
        value={draft}
        aria-label={`New name for ${value}`}
        autoFocus
        disabled={busy}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') setEditing(false);
        }}
      />
      <Button disabled={busy || !draft.trim()} onClick={commit}>
        {busy ? 'Saving…' : 'Save'}
      </Button>
      <Button variant="ghost" disabled={busy} onClick={() => setEditing(false)}>
        Cancel
      </Button>
    </span>
  );
}
